import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLazyGetJobMatchingStatusQuery } from "@js/apps/jobs/api";
import { Snackbar } from "@js/components/snackbar";

const POLLING_INTERVAL_MS = 2000;
// ADA may not pick the job up at all (e.g. the job sync is disabled), so we stop waiting at some point
const MAX_POLLING_ATTEMPTS = 60;

type UsePollingJobMatchingStatusProps = {
  jobId?: number;
};

export const usePollingJobMatchingStatus = ({
  jobId,
}: UsePollingJobMatchingStatusProps) => {
  const timeoutRef = useRef<number>(undefined);
  const isPollingInitializedRef = useRef(false);
  const pollingAttemptsRef = useRef(0);
  const [isSmartMatchingError, setIsSmartMatchingError] = useState(false);
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const [getJobMatchingStatus, { data }] = useLazyGetJobMatchingStatusQuery();
  const analyzingProfilesCount = useMemo(
    () => data?.analyzing_profiles,
    [data?.analyzing_profiles],
  );

  const isSmartMatchingLoading =
    !isSmartMatchingError &&
    !isPollingTimedOut &&
    data?.ada_matching_status !== ENUMS.ADAMatchingStatus.COMPLETED;

  const handleMatchingError = useCallback(() => {
    setIsSmartMatchingError(true);
    Snackbar.error("Something went wrong during smart matching");
  }, []);

  const pollJobMatchingStatus = useCallback(
    async (id: number) => {
      try {
        const status = await getJobMatchingStatus({ id }).unwrap();

        if (status.ada_matching_status === ENUMS.ADAMatchingStatus.COMPLETED) {
          return;
        }

        // ADA couldn't embed the job, so there are no matches to wait for
        if (status.ada_matching_status === ENUMS.ADAMatchingStatus.FAILED) {
          handleMatchingError();
          return;
        }

        pollingAttemptsRef.current += 1;
        if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
          setIsPollingTimedOut(true);
          return;
        }

        timeoutRef.current = window.setTimeout(
          // eslint-disable-next-line react-hooks/immutability
          () => pollJobMatchingStatus(id),
          POLLING_INTERVAL_MS,
        );
      } catch (_error) {
        handleMatchingError();
      }
    },
    [getJobMatchingStatus, handleMatchingError],
  );

  useEffect(() => {
    if (!jobId || isPollingInitializedRef.current) {
      return;
    }

    isPollingInitializedRef.current = true;
    void pollJobMatchingStatus(jobId);
  }, [jobId, pollJobMatchingStatus]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return {
    isSmartMatchingLoading,
    analyzingProfilesCount,
  };
};
